import { Injectable } from '@angular/core';
import { DateHelper, DateType } from './date-helper';
import { Token } from './model/token.interface';

import * as _ from 'lodash-es';

class TokenCacheItem {
  valuePromise: Promise<string>;

  private expirationDate: string;

  constructor(token: Token) {
    this.expirationDate = this.getExpirationWithGraceTime(token.created, token.expires);
    this.valuePromise = Promise.resolve(token.value);
  }

  isExpired(currentDate: DateType): boolean {
    return DateHelper.new(currentDate).isAfter(this.expirationDate);
  }

  private getExpirationWithGraceTime(creationDate: Date, expirationDate: Date): string {
    const graceTime = 1000; // 1 sec
    const startDate = DateHelper.new(creationDate);
    const endDate = DateHelper.new(expirationDate);
    const difference = endDate.diff(startDate);

    if (graceTime < difference) {
      return DateHelper.new()
        .add(difference - graceTime)
        .toISOString();
    } else {
      DateHelper.new().add(difference).toISOString();
    }
  }
}

interface TokenCache {
  [hash: number]: TokenCacheItem;
}

/**
 * Caches authentication tokens (value as promise and expiration date) in a map.
 */
@Injectable({ providedIn: 'root' })
export class TokenCacheService {
  private tokenCache: TokenCache;

  constructor() {
    this.tokenCache = {};
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Public
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  clear(): void {
    this.tokenCache = {};
  }

  setToken(key: number, token: Token): void {
    if (_.isEmpty(token.value)) {
      return;
    }

    _.set(this.tokenCache, key, new TokenCacheItem(token));
  }

  getToken(key: number): TokenCacheItem {
    return _.get(this.tokenCache, key, null);
  }

  getTokenValue(key: number): Promise<string> {
    const token = this.getToken(key);
    return _.isEmpty(token) ? null : token.valuePromise;
  }

  getTokenValueIfNotExpiredUntil(key: number, date: DateType): Promise<string> {
    const token = this.getToken(key);
    if (_.isEmpty(token)) {
      return null;
    } else {
      return token.isExpired(date) ? null : token.valuePromise;
    }
  }

  updateTokenValue(key: number, value$: Promise<string>): void {
    const token = this.getToken(key);
    if (!_.isEmpty(token)) {
      token.valuePromise = value$;
    }
  }
}
